import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VideocamIcon from '@mui/icons-material/Videocam';

import { activeAdminTenantId, canWriteTenant, useAuth } from '../../auth/AuthContext';
import { apiClient } from '../../api/client';
import * as api from '../../api/appliances';
import { guessCameraType, suggestedCameraName } from '../../util/cameraNames';
import {
  liveFrameMessage,
  POLL_MS_GRID,
  useLiveFrame,
  useLivePreviewReachable,
} from '../../components/useLiveFrame';
import { SortHeader } from '../../components/SortHeader';
import { SearchableSelect, sortOptions } from '../../components/SearchableSelect';
import { useSortedRows } from '../../util/tableSort';

const API_BASE =
  apiClient.defaults.baseURL || window.location.origin;

// Live check is LOCAL-ONLY (privacy): frames come straight from the appliance's
// localhost preview server (agent livepreview.go) — never S3/the backend, so a
// camera can't be viewed from a remote laptop. An HTTPS page can reach
// http://localhost (secure-context exception) but not a LAN IP (mixed content),
// so this only works when the browser is ON the appliance.

// Per OS+ARCH — matches the S3 keys the agent bundles publish to and the API
// serves (edge-agent-<os>-<arch>). A Linux box may be x64 or arm64; a Mac is
// Intel or Apple Silicon.
const INSTALLERS: { target: string; label: string }[] = [
  { target: 'windows-amd64', label: 'Windows (x64)' },
  { target: 'linux-amd64', label: 'Linux (x64)' },
  { target: 'linux-arm64', label: 'Linux (ARM64)' },
  { target: 'macos-arm64', label: 'macOS (Apple Silicon)' },
  { target: 'macos-amd64', label: 'macOS (Intel)' },
];

interface ApplianceRow {
  a: api.Appliance;
  cams: api.Camera[];
  online: boolean;
}
const NO_APPLIANCE_ROWS: ApplianceRow[] = [];
const APPLIANCE_SORT = {
  name: (r: ApplianceRow) => r.a.label,
  status: (r: ApplianceRow) => (r.online ? 'Online' : 'Offline'),
  os: (r: ApplianceRow) => r.a.os_name ?? null,
  agent: (r: ApplianceRow) => r.a.agent_version ?? null,
  cameras: (r: ApplianceRow) => r.cams.length,
};

const CAMERA_SOURCE_OPTIONS = [
  { value: 'device', label: 'Detected device' },
  { value: 'rtsp', label: 'Wi-Fi camera (RTSP)' },
];
const CAMERA_TYPE_OPTIONS = [
  { value: 'gopro', label: 'GoPro' },
  { value: 'ordro', label: 'Ordro' },
  { value: 'generic', label: 'Generic' },
];
const resolutionOptions = (modes: string[]) =>
  modes.map((m) => ({ value: m, label: m.replace('x', ' × ') }));

/** What happens with an available agent update, for the chip's tooltip. */
export function updateHint(running: string, release: api.AgentRelease): string {
  if (release.manual_reason) {
    return `Needs a manual update: ${release.manual_reason}. Download the new bundle below and replace the folder on the box.`;
  }
  if (api.isNewerVersion(api.FIRST_SELF_UPDATING_AGENT, running)) {
    return `This box runs ${running}, which can't update itself. Download the new bundle below and replace the folder on the box once — after that, updates install themselves.`;
  }
  return 'The box installs it itself when nothing is recording or uploading (checked at startup and every few hours).';
}

export function TenantAppliances() {
  const { user } = useAuth();
  const tenantId = activeAdminTenantId(user);
  const canWrite = canWriteTenant(user, tenantId);

  const [appliances, setAppliances] = useState<api.Appliance[] | null>(null);
  const [cameras, setCameras] = useState<api.Camera[]>([]);
  // Newest published agent, to flag boxes that are behind (EDGE-SELF-UPDATE).
  const [release, setRelease] = useState<api.AgentRelease | null>(null);
  useEffect(() => {
    api.agentRelease().then(setRelease).catch(() => setRelease(null));
  }, []);
  const [error, setError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [code, setCode] = useState<api.EnrollmentToken | null>(null);
  const [camFor, setCamFor] = useState<api.Appliance | null>(null);
  const [renameFor, setRenameFor] = useState<api.Appliance | null>(null);
  const [renameVal, setRenameVal] = useState('');

  const reload = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [appl, cams] = await Promise.all([
        api.listAppliances(tenantId),
        api.listCameras(tenantId).catch(() => [] as api.Camera[]),
      ]);
      setAppliances(appl);
      setCameras(cams);
      setError(null);
    } catch {
      setError('Could not load appliances.');
    }
  }, [tenantId]);

  useEffect(() => {
    void reload();
    // Poll so an appliance's online/offline state stays fresh as its
    // heartbeat lands (or stops).
    const id = setInterval(() => void reload(), 5000);
    return () => clearInterval(id);
  }, [reload]);

  const camerasByAppliance = useMemo(() => {
    const m = new Map<string, api.Camera[]>();
    for (const c of cameras) {
      const arr = m.get(c.appliance_id) ?? [];
      arr.push(c);
      m.set(c.appliance_id, arr);
    }
    return m;
  }, [cameras]);

  const applianceRows = useMemo(
    () =>
      appliances
        ? appliances.map((a) => ({
            a,
            cams: camerasByAppliance.get(a.id) ?? [],
            online: api.isApplianceOnline(a),
          }))
        : NO_APPLIANCE_ROWS,
    [appliances, camerasByAppliance],
  );
  const { sort, sorted } = useSortedRows(applianceRows, APPLIANCE_SORT, 'name');

  const openAdd = async () => {
    setAddOpen(true);
    setCode(null);
    if (tenantId) {
      try {
        setCode(await api.issueApplianceCode(tenantId));
      } catch {
        setError('Could not mint an enrollment code.');
      }
    }
  };

  const retire = async (a: api.Appliance) => {
    if (!tenantId) return;
    await api.retireAppliance(tenantId, a.id);
    void reload();
  };

  const doRename = async () => {
    if (!tenantId || !renameFor || !renameVal.trim()) return;
    await api.renameAppliance(tenantId, renameFor.id, renameVal.trim());
    setRenameFor(null);
    void reload();
  };

  if (!tenantId) {
    return <Alert severity="info">Select a tenant to manage its edge appliances.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Edge appliances
          </Typography>
          <Typography variant="body2" color="text.secondary">
            On-site boxes that receive camera video and record it in sync with a
            capture session. Install the agent from here, then register its cameras.
          </Typography>
        </Box>
        {canWrite && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add appliance
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortHeader label="Name" col="name" sort={sort} />
              <SortHeader label="Status" col="status" sort={sort} />
              <SortHeader label="OS" col="os" sort={sort} />
              <SortHeader label="Agent" col="agent" sort={sort} />
              <SortHeader label="Cameras" col="cameras" sort={sort} />
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {appliances === null ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={22} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : appliances.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No appliances yet. Click “Add appliance” to install one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sorted.map(({ a, online, cams }) => {
                return (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.label}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          size="small"
                          label={online ? 'Online' : 'Offline'}
                          color={online ? 'success' : 'default'}
                          variant={online ? 'filled' : 'outlined'}
                        />
                        {/* Recorded footage still on the appliance's disk. The
                            clip is safe — it is uploaded after the fact, not
                            streamed — but a backlog means a case is not in the
                            portal yet, and that should be visible here rather
                            than discovered when someone goes looking for it. */}
                        {a.pending_clips && a.pending_clips.count > 0 && (
                          <Tooltip title={pendingClipsHint(a.pending_clips)}>
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`${a.pending_clips.count} to upload`}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{a.os_name ?? '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{a.agent_version ?? '—'}</span>
                        {release?.latest_version &&
                          a.agent_version &&
                          api.isNewerVersion(release.latest_version, a.agent_version) && (
                            <Tooltip title={updateHint(a.agent_version, release)}>
                              <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                label={`Update: ${release.latest_version}`}
                              />
                            </Tooltip>
                          )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<VideocamIcon fontSize="small" />}
                        onClick={() => setCamFor(a)}
                      >
                        {cams.length}
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      {canWrite && (
                        <>
                          <Tooltip title="Rename">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setRenameVal(a.label);
                                setRenameFor(a);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Retire">
                            <IconButton size="small" onClick={() => retire(a)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddApplianceDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        code={code}
      />
      <Dialog
        open={!!renameFor}
        onClose={() => setRenameFor(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename appliance</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            placeholder="e.g. OR-3 Cart"
            value={renameVal}
            onChange={(e) => setRenameVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void doRename();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={doRename} disabled={!renameVal.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {camFor && (
        <CamerasDialog
          tenantId={tenantId}
          appliance={camFor}
          cameras={camerasByAppliance.get(camFor.id) ?? []}
          canWrite={canWrite}
          onClose={() => setCamFor(null)}
          onChanged={reload}
        />
      )}
    </Stack>
  );
}

function AddApplianceDialog({
  open,
  onClose,
  code,
}: {
  open: boolean;
  onClose: () => void;
  code: api.EnrollmentToken | null;
}) {
  const [avail, setAvail] = useState<Record<string, boolean> | null>(null);
  useEffect(() => {
    if (!open) return;
    setAvail(null);
    api.installerStatus().then(setAvail).catch(() => setAvail({}));
  }, [open]);
  const anyPublished = avail && Object.values(avail).some(Boolean);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Install an edge appliance</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          Install the agent on the on-site PC (Windows, Linux, or macOS), then
          give it this one-time code. It will appear as <b>Online</b> here once
          it connects.
        </Typography>

        <Typography variant="overline" color="text.secondary">
          1 · Enrollment code
        </Typography>
        <Box sx={{ mb: 2 }}>
          {code ? (
            <Typography
              variant="h4"
              sx={{ fontFamily: 'monospace', letterSpacing: 3, fontWeight: 700 }}
            >
              {code.code.slice(0, 4)}-{code.code.slice(4)}
            </Typography>
          ) : (
            <CircularProgress size={22} />
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            Valid for 10 minutes. Shown once.
          </Typography>
        </Box>

        <Typography variant="overline" color="text.secondary">
          2 · Download the agent for the on-site PC
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 1, flexWrap: 'wrap' }}>
          {INSTALLERS.map((i) => {
            const ready = avail?.[i.target];
            return (
              <Tooltip
                key={i.target}
                title={ready ? '' : 'No published installer yet — build from source (below)'}
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!ready}
                    href={
                      ready
                        ? `${API_BASE}/api/v1/appliances/installer?target=${i.target}` +
                          (code ? `&code=${code.code}` : '')
                        : undefined
                    }
                  >
                    {i.label}
                  </Button>
                </span>
              </Tooltip>
            );
          })}
        </Stack>

        <Typography variant="overline" color="text.secondary">
          3 · Run it
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Unzip the download, then double-click <b>start.command</b> (macOS) or{' '}
          <b>start.bat</b> (Windows) — or in a terminal run{' '}
          <code>./run.sh</code> (macOS/Linux) / <code>run.bat</code> (Windows).
          Your server and the code above are <b>baked into the download</b>, so
          there's nothing to type; the launchers also clear the macOS quarantine
          (no <code>chmod</code> needed). The appliance enrolls and comes{' '}
          <b>Online</b> here.
        </Typography>
        {avail && !anyPublished && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No prebuilt installers are published yet. Build the self-contained
            bundles (agent + ffmpeg + mediamtx) for every OS/arch:
            <Box
              component="pre"
              sx={{ m: '0.5rem 0 0', p: 1, bgcolor: 'action.hover', borderRadius: 1,
                    fontSize: '0.75rem', overflowX: 'auto' }}
            >
{`edge/build/build.sh                          # → edge/build/dist/*
edge/build/build.sh --publish s3://<bucket>  # build + publish`}
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Pick a sensible default capture resolution from a camera's advertised modes
 *  (largest first): the largest that fits within 1280×720, so we never default
 *  a 4K camera to a size its bus can't sustain. Empty when no modes are known. */
function defaultCaptureSize(modes: string[]): string {
  const within720 = modes.filter((m) => {
    const [w, h] = m.split('x').map(Number);
    return w <= 1280 && h <= 720;
  });
  return within720[0] ?? modes[modes.length - 1] ?? '';
}

function CamerasDialog({
  tenantId,
  appliance,
  cameras,
  canWrite,
  onClose,
  onChanged,
}: {
  tenantId: string;
  appliance: api.Appliance;
  cameras: api.Camera[];
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const detected = appliance.detected_cameras ?? [];
  const [liveCam, setLiveCam] = useState<api.Camera | null>(null);
  const [name, setName] = useState('');
  const [cameraType, setCameraType] = useState<api.CameraType>('generic');
  // Where the camera's video comes from: a local capture device the agent reads
  // (USB / built-in — identical to the agent, so we don't distinguish), or a
  // network camera the agent PULLS over Wi-Fi via RTSP (ORDRO EP8).
  const [source, setSource] = useState<'device' | 'rtsp'>('device');
  const [devicePath, setDevicePath] = useState('');
  const [rtspUrl, setRtspUrl] = useState('');
  // The agent reports network cameras it VERIFIED on the appliance's second
  // network (EDGE-RTSP-AUTODISCOVER) in the same detected list as USB devices,
  // distinguished by an rtsp:// device_path. Split them: a network camera must
  // not appear in the local-device dropdown, and vice versa.
  const discoveredRtsp = detected.filter((d) => d.device_path.startsWith('rtsp://'));
  const localDetected = detected.filter((d) => !d.device_path.startsWith('rtsp://'));
  // Typing the URL by hand stays available — for a camera that doesn't answer
  // discovery, or one on a network the appliance can't see yet.
  const [rtspManual, setRtspManual] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  // Names already registered on this appliance, so an auto-filled name never
  // collides with one (two identical cameras in a room is the normal case).
  const takenNames = cameras.map((c) => c.name);
  const [captureSize, setCaptureSize] = useState('');
  const [busy, setBusy] = useState(false);
  const captureModes =
    detected.find((d) => d.device_path === devicePath)?.modes ?? [];
  const isRtsp = source === 'rtsp';
  const canAdd = !!name.trim() && (isRtsp ? !!rtspUrl.trim() : true);

  const add = async () => {
    if (!canAdd) return;
    setBusy(true);
    try {
      await api.createCamera(tenantId, {
        appliance_id: appliance.id,
        name: name.trim(),
        camera_type: cameraType,
        connection: isRtsp ? 'rtsp' : 'usb',
        stream_key: null,
        // rtsp stores the camera's URL in device_path (the agent reads it
        // directly); local cameras store the OS capture device path.
        device_path: (isRtsp ? rtspUrl : devicePath).trim() || null,
        capture_size: isRtsp ? null : captureSize || null,
      });
      setName('');
      setDevicePath('');
      setRtspUrl('');
      setCaptureSize('');
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c: api.Camera) => {
    await api.retireCamera(tenantId, c.id);
    await onChanged();
  };

  const [savingRes, setSavingRes] = useState<string | null>(null);
  const changeResolution = async (c: api.Camera, size: string) => {
    setSavingRes(c.id);
    try {
      await api.updateCamera(tenantId, c.id, { capture_size: size });
      await onChanged();
    } finally {
      setSavingRes(null);
    }
  };
  // Resolution options for a registered camera: its detected modes, plus its
  // current value if that mode is no longer advertised (e.g. camera offline).
  const resOptions = (c: api.Camera): string[] => {
    const modes = detected.find((d) => d.device_path === c.device_path)?.modes ?? [];
    const opts = [...modes];
    if (c.capture_size && !opts.includes(c.capture_size)) opts.unshift(c.capture_size);
    return opts;
  };

  return (
    <>
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cameras — {appliance.label}</DialogTitle>
      <DialogContent dividers>
        {cameras.length > 1 && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<VideocamIcon fontSize="small" />}
              onClick={() => setCheckAll(true)}
            >
              Test all cameras
            </Button>
          </Stack>
        )}
        <Stack spacing={1} sx={{ mb: 2 }}>
          {cameras.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No cameras registered on this appliance yet.
            </Typography>
          ) : (
            cameras.map((c) => (
              <Box key={c.id}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">
                    <b>{c.name}</b>{' '}
                    <Chip size="small" label={c.connection} sx={{ ml: 0.5 }} />
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {c.connection !== 'wifi' && resOptions(c).length > 0 && (
                      <SearchableSelect
                        label="Resolution"
                        value={c.capture_size ?? ''}
                        disabled={savingRes === c.id || !canWrite}
                        onChange={(v) => changeResolution(c, v)}
                        options={resolutionOptions(resOptions(c))}
                        keepOrder
                        sx={{ minWidth: 104 }}
                      />
                    )}
                    <Button
                      size="small"
                      startIcon={<VideocamIcon fontSize="small" />}
                      onClick={() => setLiveCam(c)}
                    >
                      Live check
                    </Button>
                    {canWrite && (
                      <IconButton size="small" onClick={() => remove(c)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
              </Box>
            ))
          )}
        </Stack>

        {canWrite && (
          <Stack spacing={1.5}>
            <Typography variant="overline" color="text.secondary">
              Add a camera
            </Typography>
            <TextField
              size="small"
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <Stack direction="row" spacing={1}>
              <SearchableSelect
                label="Source"
                value={source}
                onChange={(v) => setSource(v as 'device' | 'rtsp')}
                options={CAMERA_SOURCE_OPTIONS}
                sx={{ minWidth: 160 }}
              />
              <SearchableSelect
                label="Type"
                value={cameraType}
                onChange={(v) => setCameraType(v as api.CameraType)}
                options={CAMERA_TYPE_OPTIONS}
                sx={{ minWidth: 120 }}
              />
            </Stack>
            {isRtsp && discoveredRtsp.length > 0 && !rtspManual ? (
              <SearchableSelect
                label="Network camera"
                helperText="Found on the appliance's camera network and confirmed streaming."
                value={rtspUrl}
                onChange={(v) => {
                  if (v === '__manual__') {
                    setRtspManual(true);
                    setRtspUrl('');
                    return;
                  }
                  setRtspUrl(v);
                  const hit = discoveredRtsp.find((d) => d.device_path === v);
                  if (hit && !name.trim()) {
                    // Name it after the camera's own access point (its SSID),
                    // de-duplicated — typing a name by hand was the last manual
                    // step in this flow and the one that produced "camera2".
                    setName(suggestedCameraName(hit.label, takenNames));
                    setCameraType(guessCameraType(hit.label));
                  }
                }}
                // Discovered cameras alphabetically, the manual escape hatch last.
                options={[
                  ...sortOptions(
                    discoveredRtsp.map((d) => ({
                      value: d.device_path,
                      label: `${d.label}${d.modes?.length ? ` · ${d.modes[0]}` : ''}`,
                    })),
                  ),
                  { value: '__manual__', label: 'Enter a URL manually…' },
                ]}
                keepOrder
                fullWidth
              />
            ) : isRtsp ? (
              <TextField
                size="small"
                label="RTSP URL"
                placeholder="rtsp://192.168.x.x:554/…"
                helperText={
                  discoveredRtsp.length > 0
                    ? 'Typed by hand. Clear it to pick a discovered camera instead.'
                    : discoveryHint(appliance.camera_discovery)
                }
                value={rtspUrl}
                onChange={(e) => setRtspUrl(e.target.value)}
                fullWidth
              />
            ) : localDetected.length > 0 ? (
              <SearchableSelect
                label="Camera"
                helperText="Detected on the appliance."
                value={devicePath}
                onChange={(v) => {
                  setDevicePath(v);
                  const hit = localDetected.find((d) => d.device_path === v);
                  if (hit && !name.trim()) {
                    setName(suggestedCameraName(hit.label, takenNames));
                  }
                  // Default the resolution to the largest mode <=720p (avoids
                  // defaulting a 4K cam to a size it can't sustain).
                  setCaptureSize(defaultCaptureSize(hit?.modes ?? []));
                }}
                options={localDetected.map((d) => ({ value: d.device_path, label: d.label }))}
                fullWidth
              />
            ) : (
              <TextField
                size="small"
                label="Device (capture input)"
                helperText="No cameras detected — check the appliance is online, or type a dshow name / v4l2 path."
                value={devicePath}
                onChange={(e) => setDevicePath(e.target.value)}
                fullWidth
              />
            )}
            {!isRtsp && captureModes.length > 0 && (
              <SearchableSelect
                label="Resolution"
                helperText="Higher looks sharper but a USB camera may not sustain it — drop it if the live view stutters."
                value={captureSize}
                onChange={setCaptureSize}
                options={resolutionOptions(captureModes)}
                keepOrder
                fullWidth
              />
            )}
            <Button
              variant="contained"
              onClick={add}
              disabled={busy || !canAdd}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add camera
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
    {liveCam && (
      <LiveCheckDialog camera={liveCam} onClose={() => setLiveCam(null)} />
    )}
    {checkAll && (
      <LiveCheckAllDialog cameras={cameras} onClose={() => setCheckAll(false)} />
    )}
    </>
  );
}

function LiveCheckDialog({
  camera,
  onClose,
}: {
  camera: api.Camera | null;
  onClose: () => void;
}) {
  const reachable = useLivePreviewReachable();
  // Shares the polling hook with the all-cameras grid so both report the same
  // states — in particular "recording", which the old <img>-based poll could
  // not see at all (an image load failure looks the same as a warm-up).
  const { status, src } = useLiveFrame(camera?.id ?? '', !!camera && reachable !== false);
  if (!camera) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Live check — {camera.name}</DialogTitle>
      <DialogContent dividers>
        {reachable === false ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            <strong>Open this page on the appliance to test the camera.</strong>
            <br />
            For privacy, Live check runs only on the on-site appliance — a camera
            can’t be viewed from another computer. Log in on the appliance itself
            and try again.
          </Alert>
        ) : status === 'recording' ? (
          <Alert severity="warning">
            <strong>This camera is recording.</strong>
            <br />
            A camera can serve only one connection at a time, so testing it now
            would put the recording at risk. The check is available again as
            soon as the session ends.
          </Alert>
        ) : src ? (
          <img
            src={src}
            alt={`Live check for ${camera.name}`}
            style={{
              width: '100%',
              borderRadius: 4,
              backgroundColor: 'black',
              display: 'block',
            }}
          />
        ) : (
          <Box
            sx={{
              height: 260,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'black',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: 'grey.400' }}>
              {liveFrameMessage(status)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/** One camera's tile in the all-cameras check. */
function LiveCheckTile({ camera }: { camera: api.Camera }) {
  // Slower than the single-camera view on purpose — see POLL_MS_GRID.
  const { status, src } = useLiveFrame(camera.id, true, POLL_MS_GRID);
  const msg = liveFrameMessage(status);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16 / 9',
          bgcolor: 'black',
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {src ? (
          <img
            src={src}
            alt={`Live check for ${camera.name}`}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <Typography
            variant="caption"
            sx={{ color: 'grey.400', px: 1, textAlign: 'center' }}
          >
            {msg}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
        <Typography variant="caption" noWrap sx={{ fontWeight: 700, minWidth: 0 }}>
          {camera.name}
        </Typography>
        {status === 'live' && (
          <Chip size="small" color="success" label="live" sx={{ height: 18, fontSize: 11 }} />
        )}
        {status === 'recording' && (
          <Chip size="small" color="warning" label="recording" sx={{ height: 18, fontSize: 11 }} />
        )}
        {(status === 'unknown-camera' || status === 'unreachable') && (
          <Chip size="small" color="error" label="no signal" sx={{ height: 18, fontSize: 11 }} />
        )}
      </Stack>
    </Box>
  );
}

/**
 * Check every registered camera at once — the question an operator actually
 * has before a case ("is each camera alive and pointed the right way?"), which
 * one-at-a-time checking answers slowly.
 *
 * Each tile polls its own camera; the appliance runs an independent burst per
 * camera and stops each one a few seconds after its tile stops polling, so
 * closing this releases every camera without an explicit stop.
 *
 * A camera that is RECORDING is refused by the appliance and shown as such
 * rather than opened: a camera serves one consumer, so previewing it would put
 * the recording at risk. That is why this is safe to press mid-session — the
 * other cameras still answer.
 */
function LiveCheckAllDialog({
  cameras,
  onClose,
}: {
  cameras: api.Camera[];
  onClose: () => void;
}) {
  const reachable = useLivePreviewReachable();
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Live check — all cameras</DialogTitle>
      <DialogContent dividers>
        {reachable === false ? (
          <Alert severity="info">
            <strong>Open this page on the appliance to test the cameras.</strong>
            <br />
            For privacy, Live check runs only on the on-site appliance — cameras
            can’t be viewed from another computer.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Every registered camera, live. A camera that is recording is left
              alone — it can serve only one connection at a time.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
              }}
            >
              {cameras.map((c) => (
                <LiveCheckTile key={c.id} camera={c} />
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * What to tell the operator when no network camera was discovered.
 *
 * "Not found" is not actionable on its own: an adapter that isn't joined to
 * the camera's Wi-Fi needs plugging in, while one that IS joined but gets no
 * stream means the camera is off or on a path we don't know. The agent
 * distinguishes those, so the UI should too — otherwise the same empty box
 * stands for three different problems.
 */
function discoveryHint(d: api.CameraDiscovery | null | undefined): string {
  const manual =
    " You can still enter the camera's URL by hand.";
  if (!d) {
    return (
      "The camera's stream URL (e.g. the ORDRO EP8 on its Wi-Fi). This" +
      ' appliance is on an agent older than 0.3.14, so it cannot look for the' +
      ' camera by itself.'
    );
  }
  switch (d.state) {
    case 'no_camera_network': {
      const on = d.ssids?.length ? ` Adapters are on: ${d.ssids.join(', ')}.` : '';
      return (
        `No camera found — no Wi-Fi adapter on this appliance is joined to a` +
        ` ${d.ssid_prefix}… network (the camera's own Wi-Fi).${on}` +
        ` Connect the second adapter to the camera, then reopen this dialog.` +
        manual
      );
    }
    case 'camera_network_only':
      return (
        `The appliance IS on the camera's ${d.ssid_prefix}… Wi-Fi, but nothing` +
        ' answered as a stream — the camera may be off, asleep, or already in' +
        ' use by another app (it serves one connection at a time).' + manual
      );
    case 'no_wifi_info':
      return (
        'Could not read this appliance\'s Wi-Fi networks, so the camera could' +
        ' not be looked up automatically.' + manual
      );
    default:
      return "The camera's stream URL (e.g. the ORDRO EP8 on its Wi-Fi).";
  }
}

/**
 * Explains an upload backlog. The age is the part that matters: a clip waiting
 * two minutes is a network blip, one waiting two days means the appliance has
 * been offline or the upload keeps failing and nobody noticed.
 */
function pendingClipsHint(p: api.PendingClips): string {
  const base =
    `${p.count} recorded clip${p.count === 1 ? '' : 's'} on this appliance ` +
    'not uploaded yet. The recording is safe on its disk and retries ' +
    'automatically.';
  if (!p.oldest_at) return base;
  const ageMs = Date.now() - new Date(p.oldest_at).getTime();
  const hours = Math.floor(ageMs / 3_600_000);
  if (hours < 1) return `${base} Oldest: under an hour.`;
  if (hours < 48) return `${base} Oldest: ${hours} hour${hours === 1 ? '' : 's'} old.`;
  return `${base} Oldest: ${Math.floor(hours / 24)} days old — worth checking the network.`;
}
